import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, LogIn } from "lucide-react";
import { Waveform } from "@/components/Waveform";
import { api, errMsg, setToken } from "@/lib/api";

export default function AdminLogin() {
  const nav = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const r = await api.login(username, password);
      setToken(r.token);
      nav("/admin", { replace: true });
    } catch (err) {
      setToken("");
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grain relative grid min-h-screen bg-[#121417] md:grid-cols-2" data-testid="admin-login">
      <div className="relative z-10 hidden flex-col justify-between border-r border-white/[0.06] p-12 md:flex">
        <p className="eyebrow">Griot · Admin Console</p>
        <div>
          <Waveform state="reflecting" height={220} />
          <h1 className="mt-6 font-display text-5xl font-semibold leading-[1.02] tracking-tight">Tend the archive.<br /><span className="text-[#A1A8B3]">Tune the voice.</span></h1>
        </div>
        <p className="text-xs text-[#6E7582]">Curator access only. All actions are logged.</p>
      </div>
      <div className="relative z-10 flex items-center justify-center p-8">
        <form className="w-full max-w-sm space-y-5" onSubmit={submit}>
          <div>
            <p className="eyebrow md:hidden">Admin Console</p>
            <h2 className="font-display text-2xl font-semibold">Sign in</h2>
            <p className="mt-1 text-sm text-[#6E7582]">Curators manage every Lab from here.</p>
          </div>

          <label className="block">
            <span className="text-xs text-[#6E7582]">Username</span>
            <input
              data-testid="admin-username-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              autoFocus
              className="mt-1 w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-[#F3EFE6] focus:border-[#E07A5F] focus:outline-none"
            />
          </label>

          <label className="block">
            <span className="text-xs text-[#6E7582]">Password</span>
            <input
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className="mt-1 w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-[#F3EFE6] focus:border-[#E07A5F] focus:outline-none"
            />
          </label>

          {error && <p data-testid="admin-login-error" className="rounded-xl border border-[#E63946]/40 bg-[#E63946]/10 px-3 py-2 text-xs text-[#E63946]">{error}</p>}

          <button
            data-testid="admin-login-btn"
            type="submit"
            disabled={busy || !username || !password}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-[#E07A5F] py-3.5 font-display text-sm font-semibold text-[#121417] transition-colors hover:bg-[#F4A261] disabled:opacity-50"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <LogIn size={16} />} {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
